import { usePersistedState } from "@/lib/usePersistedState";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { MainLayout } from "./../../components/ui/main-layout/main-layout";
import { Bom } from "./../../containers/bom/bom";
import { Tasks } from "./../../containers/tasks/tasks";
import { ApplicationHeader } from "./../../containers/application-header/application-header";
import { Navigation } from "./navigation";
import { UserSection } from "./../../containers/user-section/user-section";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./../../components/ui/accordion/accordion";
import {
  ClipboardCheck,
  MessageSquareText,
  CircleCheck,
  ArrowBigRight,
  Files,
  Wrench,
} from "lucide-react";
import { Note } from "./../../containers/note/note";
import { MachineSelector } from "../machine-selector/machine-selector";
import { useWorkOrderOperation } from "../../queries/work-order/use-work-order-operation";
import { useMachine } from "@/queries/machine/use-machine";
import { ROUTES } from "@/routes";
import { SelectDevEnvironment } from "./select-dev-environment";
import { isDev } from "@/lib/utils";
import { useNote } from "@/queries/note/use-note";
import { useRefreshAuth } from "../../auth/use-refresh-auth";
import { OperatorAwarenessAccordionItem } from "../operator-awareness-accordion-item/operator-awareness-accordion-item";
import { Loader } from "@/components/ui/loader/loader";
import { useTasks } from "@/queries/tasks/use-tasks";

export const accordionItemValue = {
  tasks: "tasks",
  notes: "notes",
  operatorAwareness: "operator-awareness",
};

export function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { noActiveOperation } = useWorkOrderOperation();
  const { machine, noActiveMachine } = useMachine();
  const { isEmpty: noteIsEmpty } = useNote();
  const { isEmpty: tasksIsEmpty } = useTasks();
  useRefreshAuth();

  const [navOpen, setNavOpen] = useState(false);
  const [accordionState, setAccordionState] = usePersistedState("accordions", [
    accordionItemValue.tasks,
    accordionItemValue.notes,
    accordionItemValue.operatorAwareness,
  ]);

  const handleNoteClick = useCallback(() => {
    navigate(ROUTES.SAP_NOTE);
  }, [navigate]);

  const location = useLocation();

  useEffect(() => {
    setNavOpen(false);
  }, [location]);

  return (
    <MainLayout
      open={navOpen}
      onOpenChange={setNavOpen}
      navigationSlot={
        <div className="flex flex-col h-full justify-between">
          <Navigation />
          <UserSection />
        </div>
      }
      navigationOverlaySlot={isDev() && <SelectDevEnvironment />}
      headerSlot={<ApplicationHeader />}
      sidebarSlot={
        <>
          {!noActiveMachine && !noActiveOperation && (
            <div className="py-5 px-2 flex flex-col justify-between h-full text-center">
              <div>
                <Link
                  to={ROUTES.COMPLETE_OPERATION}
                  className="flex flex-col items-center text-sm"
                >
                  <CircleCheck className="h-7 w-7" />
                  <p className="truncate whitespace-normal w-full">
                    {t("sidebar.complete")}
                  </p>
                </Link>
              </div>
              <div className="flex flex-col gap-4">
                <Link
                  to={ROUTES.DOCUMENTS()}
                  className="flex flex-col items-center text-sm"
                >
                  <Files className="h-7 w-7" />
                  <p className=" truncate whitespace-normal w-full">
                    {t("sidebar.documents")}
                  </p>
                </Link>
                <Link
                  to={ROUTES.ROUTING}
                  className="flex flex-col items-center text-sm mt-auto"
                >
                  <ArrowBigRight className="h-7 w-7" />
                  <p className="truncate whitespace-normal w-full">
                    {t("sidebar.routing")}
                  </p>
                </Link>
                <Link
                  to={ROUTES.DECLARE_DEVIATION}
                  className="flex flex-col items-center text-sm mt-auto"
                >
                  <Wrench className="h-7 w-7" />
                  <p className="truncate whitespace-normal w-full">
                    {t("sidebar.deviation")}
                  </p>
                </Link>
              </div>
            </div>
          )}
        </>
      }
    >
      {noActiveMachine && (
        <div className="p-4 flex flex-col gap-4">
          <p className="text-lg font-semibold">{t("home.noMachineHeader")}</p>
          <div>
            <MachineSelector />
          </div>
        </div>
      )}
      {!noActiveMachine && noActiveOperation && (
        <div className="flex flex-col justify-center items-center h-full gap-6">
          <div className="flex flex-col justify-center items-center gap-6">
            <Loader className="h-3 w-3 text-gray-400" />
            <span className="text-xl font-semibold">
              {machine?.EquipmentNId}
            </span>
            <p className="text-xl text-gray-400">{t("noOperation.header")}</p>
          </div>
        </div>
      )}
      {!noActiveOperation && (
        <div className="flex justify-between gap-x-4 h-full p-4">
          <div className="w-[460px] flex-shrink-0">
            <Accordion
              type="multiple"
              value={accordionState}
              onValueChange={setAccordionState}
              className="flex flex-col gap-4 h-full"
            >
              <OperatorAwarenessAccordionItem
                accordionValue={accordionItemValue.operatorAwareness}
              />
              {!tasksIsEmpty && (
                <AccordionItem value={accordionItemValue.tasks}>
                  <AccordionTrigger>
                    <div className="flex gap-2 items-center">
                      <ClipboardCheck className="h-5 w-5" />
                      <p>{t("task.accordionHeader")}</p>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <Tasks />
                  </AccordionContent>
                </AccordionItem>
              )}
              {!noteIsEmpty && (
                <AccordionItem
                  value={accordionItemValue.notes}
                  className="flex flex-col overflow-hidden shrink grow"
                >
                  <AccordionTrigger>
                    <div className="flex gap-2 items-center">
                      <MessageSquareText className="h-5 w-5" />
                      <p>{t("notes.accordionHeader")}</p>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent scroll>
                    <Note onClick={handleNoteClick} />
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>
          </div>
          <div className="w-full overflow-auto">
            <Bom />
          </div>
        </div>
      )}
      {/* Render sub routes for dialogs sheets etc */}
      <Outlet />
    </MainLayout>
  );
}
